/**
 * Digital signature capture component using HTML5 Canvas.
 * Required before form submission.
 *
 * Validates: Requirements 9.12
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import { Btn } from '../ui';

interface SignatureCaptureProps {
    signatureDataUrl: string;
    onCapture: (dataUrl: string) => void;
}

export const SignatureCapture = ({
    signatureDataUrl,
    onCapture,
}: SignatureCaptureProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);

    // Set up canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size to match display size
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);

        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, rect.width, rect.height);

        // Drawing style
        ctx.strokeStyle = '#1a202c';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }, []);

    const getPosition = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            const canvas = canvasRef.current;
            if (!canvas) return { x: 0, y: 0 };
            const rect = canvas.getBoundingClientRect();
            if ('touches' in e) {
                const touch = e.touches[0];
                return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
            }
            return { x: e.clientX - rect.left, y: e.clientY - rect.top };
        },
        [],
    );

    const startDrawing = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            e.preventDefault();
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d');
            if (!ctx) return;
            const pos = getPosition(e);
            ctx.beginPath();
            ctx.moveTo(pos.x, pos.y);
            setIsDrawing(true);
            setHasDrawn(true);
        },
        [getPosition],
    );

    const draw = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            e.preventDefault();
            if (!isDrawing) return;
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d');
            if (!ctx) return;
            const pos = getPosition(e);
            ctx.lineTo(pos.x, pos.y);
            ctx.stroke();
        },
        [isDrawing, getPosition],
    );

    const stopDrawing = useCallback(() => {
        if (!isDrawing) return;
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (canvas) {
            onCapture(canvas.toDataURL('image/png'));
        }
    }, [isDrawing, onCapture]);

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!ctx || !canvas) return;
        const rect = canvas.getBoundingClientRect();
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.strokeStyle = '#1a202c';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        setHasDrawn(false);
        onCapture('');
    };

    return (
        <div className="pm-inspection-section">
            <h3 className="pm-inspection-section-title">Digital Signature</h3>
            <p className="pm-inspection-signature-hint">
                Sign below to confirm the PM service has been completed.
                {!signatureDataUrl && (
                    <span className="pm-inspection-required"> (Required)</span>
                )}
            </p>

            <div className="pm-inspection-signature-wrapper">
                <canvas
                    ref={canvasRef}
                    className="pm-inspection-signature-canvas"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                />
            </div>

            {hasDrawn && (
                <Btn
                    variant="secondary-outlined"
                    buttonSize="sm"
                    buttonText="Clear Signature"
                    buttonOnClick={clearSignature}
                />
            )}
        </div>
    );
};
